import React from "react";
import { ReactFlow } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useTranslation } from "react-i18next";
import AccountNode from "./AccountNode";
import DirectionEdge from "./DirectionEdge";
import { buttonClasses } from "../../common/tailwindClassnames";
import { getConnectionStatus } from "../getConnectionStatus";
import { FLOW_COLORS, FlowDirectionKey } from "./flowMapTypes";

const nodeTypes = { account: AccountNode };
const edgeTypes = { direction: DirectionEdge };

type ConnectionFlowMapProps = {
    nodes: any[];
    edges: any[];
    destinationCount: number;
    connectionStatus?: string;
    onSelectAccount: (integrationId: string) => void;
    onSelectFlow: (destinationId: string, direction: FlowDirectionKey) => void;
    onAddDestination: () => void;
    // Mirrors the header's Connection Enabled switch — a disabled connection moves
    // no data, so the whole graph is muted and the state is called out in words.
    connectionEnabled?: boolean;
    // Pulses the "+ Add destination" button — set right after creating a new
    // connection so the user's next step is obvious.
    highlightAddDestination?: boolean;
};

const FIT_VIEW_OPTIONS = { padding: 0.15, maxZoom: 1 };

const LEGEND = [
    { label: "Enabled", className: "border-t-2 border-connection-green w-6" },
    { label: "Disabled", className: "border-t-2 border-gray-lines w-6" },
    { label: "Not available", className: "border-t-2 border-dashed border-gray-lines w-6" },
    // Same amber "!" badge as the node/arrow indicators.
    { label: "Needs setup", className: "w-4 h-4 rounded-full bg-orange-50 text-orange border border-orange-200 text-[10px] font-bold flex items-center justify-center", glyph: "!" },
];

const ConnectionFlowMap = ({ nodes, edges, destinationCount, connectionStatus, onSelectAccount, onSelectFlow, onAddDestination, highlightAddDestination, connectionEnabled = true }: ConnectionFlowMapProps) => {
    const { t } = useTranslation();
    const mapHeight = Math.max(280, destinationCount * 132 + 80);
    // The status pill describes data flow, so it only means anything while the
    // connection is enabled — otherwise the disabled chip says it all.
    const status = connectionStatus && connectionEnabled ? getConnectionStatus(connectionStatus, true, t) : undefined;

    // The pulse stops on its own or as soon as the user clicks the button.
    const [highlight, setHighlight] = React.useState(!!highlightAddDestination);
    React.useEffect(() => {
        if (!highlightAddDestination) return;
        setHighlight(true);
        const timer = setTimeout(() => setHighlight(false), 8000);
        return () => clearTimeout(timer);
    }, [highlightAddDestination]);

    // fitView only runs on mount, so adding or removing a destination would leave
    // the viewport framed for the old graph (new nodes off-canvas). Re-fit whenever
    // the node set or the canvas height changes. maxZoom 1 keeps a one- or
    // two-node graph at natural size instead of blowing the cards up to fill it.
    const [rfInstance, setRfInstance] = React.useState<any>(null);
    const canvasRef = React.useRef<HTMLDivElement | null>(null);
    const nodeKey = nodes.map((n) => n.id).join(",");
    React.useEffect(() => {
        if (!rfInstance) return;
        // after the layout pass that follows the height change
        const t = setTimeout(() => rfInstance.fitView(FIT_VIEW_OPTIONS), 0);
        return () => clearTimeout(t);
    }, [rfInstance, nodeKey, mapHeight]);

    // React Flow fits the view against whatever size the canvas has when it
    // measures it. On a client-side navigation that measurement can land while the
    // container is still zero-width, and the fit is then computed for a canvas
    // that doesn't exist yet — the nodes end up outside the viewport and the map
    // reads as empty until a reload. A full page load never hits it, which is why
    // refreshing "fixed" it. Re-fit whenever the canvas is actually resized; the
    // map is not pannable or zoomable, so there is no user viewport to disturb.
    React.useEffect(() => {
        const el = canvasRef.current;
        if (!rfInstance || !el || typeof ResizeObserver === "undefined") return;
        // Fitting can itself nudge layout, so an unguarded observer re-enters and
        // the browser reports "ResizeObserver loop completed with undelivered
        // notifications". Only react to a size we haven't fitted for yet, and do
        // the fit on the next frame rather than inside the callback.
        let frame = 0;
        let fittedFor = "";
        const observer = new ResizeObserver(([entry]) => {
            const width = Math.round(entry.contentRect.width);
            const height = Math.round(entry.contentRect.height);
            if (width === 0 || height === 0) return;
            const size = `${width}x${height}`;
            if (size === fittedFor) return;
            fittedFor = size;
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => rfInstance.fitView(FIT_VIEW_OPTIONS));
        });
        observer.observe(el);
        return () => { cancelAnimationFrame(frame); observer.disconnect(); };
    }, [rfInstance]);
    // Arrow clicks come from the edge's own label/hit-area handler, so the
    // callback rides along in edge data. onSelectFlow is an inline arrow that
    // closes over the panel's dirty state, so it must not be captured in the
    // memo — a stale copy would skip the "discard unsaved changes?" prompt.
    // A ref keeps the handler current without rebuilding the edges every render.
    const onSelectFlowRef = React.useRef(onSelectFlow);
    onSelectFlowRef.current = onSelectFlow;
    const selectFlow = React.useCallback(
        (destinationId: string, direction: FlowDirectionKey) => onSelectFlowRef.current(destinationId, direction), []);
    const edgesWithHandler = React.useMemo(
        () => edges.map((e) => ({ ...e, data: { ...e.data, onSelect: selectFlow } })),
        [edges, selectFlow]);

    return (
        <div className="border border-gray-lines rounded-lg bg-white mb-6">
            <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-lines">
                <span className="text-lg font-bold text-off-black">{t("Flow map")}</span>
                {status && <span className={status.style}>{status.text}</span>}
                {!connectionEnabled && (
                    <span className="rounded-full bg-neutral-gray-50 text-secondary-text border border-gray-lines px-2 py-0.5 text-xs font-semibold">
                        {t("Connection Disabled")}
                    </span>
                )}
                <button
                    className={`${buttonClasses.lightGreen} ml-auto whitespace-nowrap ${
                        highlight ? "animate-pulse ring-2 ring-connection-green ring-offset-2" : ""}`}
                    onClick={() => { setHighlight(false); onAddDestination(); }}>
                    + {t("Add destination")}
                </button>
            </div>
            {/* One amber strip for whatever is keeping data from flowing. */}
            {!connectionEnabled ? (
                <div className="px-5 py-2 bg-orange-50 border-b border-orange-200 text-sm text-off-black">
                    {t("This connection is disabled, so no data is moving along these flows. Use the switch above to enable it.")}
                </div>
            ) : connectionStatus === "unhealthy" ? (
                <div className="px-5 py-2 bg-orange-50 border-b border-orange-200 text-sm text-off-black">
                    {t("This connection is unhealthy. Check the Logs tab for details.")}
                </div>
            ) : null}
            <div className={`overflow-x-auto ${connectionEnabled ? "" : "grayscale opacity-60"}`}>
                <div ref={canvasRef} style={{ height: mapHeight, minWidth: 880 }}>
                    {/* arrowheads, one per state (SVG markers can't be Tailwind-coloured) */}
                    <svg style={{ height: 0, width: 0, position: "absolute" }}>
                        <defs>
                            {Object.entries(FLOW_COLORS).map(([state, color]) => (
                                <marker key={state} id={`flow-arrow-${state}`} markerWidth="10" markerHeight="10"
                                    refX="8" refY="4" orient="auto" markerUnits="strokeWidth">
                                    <path d="M0,0 L8,4 L0,8 z" fill={color} />
                                </marker>
                            ))}
                        </defs>
                    </svg>
                    <ReactFlow
                        nodes={nodes}
                        edges={edgesWithHandler}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        nodesDraggable={false}
                        nodesConnectable={false}
                        elementsSelectable={false}
                        edgesFocusable={false}
                        panOnDrag={false}
                        zoomOnScroll={false}
                        zoomOnPinch={false}
                        zoomOnDoubleClick={false}
                        preventScrolling={false}
                        proOptions={{ hideAttribution: true }}
                        fitView
                        fitViewOptions={FIT_VIEW_OPTIONS}
                        onInit={setRfInstance}
                        onNodeClick={(_: any, n: any) => onSelectAccount(n.id)}
                    />
                </div>
            </div>
            <div className="flex flex-wrap gap-5 px-5 py-2 border-t border-gray-lines text-xs text-secondary-text">
                {LEGEND.map((l: any) => (
                    <span key={l.label} className="flex items-center gap-2">
                        <span className={l.className}>{l.glyph}</span>{t(l.label as any)}
                    </span>
                ))}
            </div>
        </div>
    );
};

export default ConnectionFlowMap;
